import { useState } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { isViewOnlyArea } from 'services/areaCodes'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import CheckBox from 'components/CheckBox'
import ImportPermissionsModal from '../ImportPermissionsModal'
import accessLevelValidator from 'validators/accessLevelValidator'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import { FaFileImport } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  Form,
  FormField,
  PermissionsContainer,
  CheckRow,
  Label,
  SubTitle,
  Input,
  SmallButton,
  WarningText,
  ErrorContainer
} from './styles'

export default function AccessLevelModal ({
  visible,
  accessLevel = null,
  accessLevels = [],
  permissions = [],
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [errorMessage, setErrorMessage] = useState (null)
  const [showImportPermissionsModal, setShowImportPermissionsModal] = useState (false)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  const isEditting = !!accessLevel
  const isCreatedByBusiness = accessLevel?.business_id === businessId
  const canEdit = !isEditting || isCreatedByBusiness

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const toggleImportPermissionsModal = () => setShowImportPermissionsModal (!showImportPermissionsModal)

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isEditting ? 'Alterar' : 'Criar'} nível de acesso</DialogTitle>
        </DialogHeader>
        <Formik
          validationSchema={accessLevelValidator}
          initialValues={{
            'businessId': businessId,
            'name': isEditting ? accessLevel.name : '',
            'description': isEditting ? accessLevel.description : '',
            'permissions': permissions.map (permission => {
              const existingPermission = accessLevel?.permissions.find (p => p.id === permission.id)
              return isEditting && existingPermission
                ? { ...existingPermission, selected: true }
                : { ...permission, selected: false }
            })
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const selectedPermissions = values.permissions.filter (p => p.selected)

              if (isEditting) {
                const { data: editedAccessLevel } = await cutileiApi.put (
                  `/access_levels/${accessLevel.id}`,
                  {...values, permissions: selectedPermissions },
                  requestConfig
                )
                handleConfirm (editedAccessLevel)
              } else {
                const { data: createdAccessLevel } = await cutileiApi.post (
                  '/access_levels',
                  {...values, permissions: selectedPermissions },
                  requestConfig
                )
                handleConfirm (createdAccessLevel)
              }

              setSubmitting (false)
              handleClose ()
            } catch (error) {
              logApiError (error)
              setErrorMessage (getApiErrorMessage (error))
              setSubmitting (false)
            }
          }}
        >
          {function AccessLevelForm ({
            values,
            errors,
            touched,
            dirty,
            isSubmitting,
            setFieldValue,
            handleChange,
            handleBlur,
            handleSubmit
          }) {
            const handleViewPermissionChanged = (value, permission, index) => {
              const isChild = permission.area_code.includes ('.')
              const isParent = !isChild

              setFieldValue (`permissions[${index}].selected`, value)

              if (value === false) {
                setFieldValue (`permissions[${index}].allow_edit`, false)
                setFieldValue (`permissions[${index}].allow_delete`, false)
              }
            
              if (isParent) {
                const updatedPermissions = values.permissions.map ((p, i) => {
                  if (p.area_code !== permission.area_code && p.area_code.startsWith (permission.area_code + '.')) {
                    return value === false 
                      ? { ...p, selected: false, allow_edit: false, allow_delete: false }
                      : { ...p, selected: true }
                  }
                  if (i === index) {
                    return value === false
                      ? { ...p, selected: false, allow_edit: false, allow_delete: false }
                      : { ...p, selected: true }
                  }
                  return p
                })
                setFieldValue ('permissions', updatedPermissions)
                return
              }

              if (isChild && value === false) {
                const parentAreaCode = permission.area_code.split ('.')[0]
                const siblings = values.permissions.filter (p => (
                  p.area_code.startsWith (parentAreaCode + '.') && p.area_code !== permission.area_code
                ))
                const allSiblingsUnselected = siblings.every (sib => !sib.selected)
                const parentIndex = values.permissions.findIndex (p => p.area_code === parentAreaCode)

                if (allSiblingsUnselected && parentIndex !== -1) {
                  const updatedPermissions = values.permissions.map ((p, i) => {
                    if (i === parentIndex) return { ...p, selected: false, allow_edit: false, allow_delete: false }
                    if (i === index) return { ...p, selected: false, allow_edit: false, allow_delete: false }
                    return p
                  })
                  setFieldValue ('permissions', updatedPermissions)
                  return
                }
                setFieldValue (`permissions[${index}].selected`, false)
              }

              if (isChild && value === true) {
                const parentAreaCode = permission.area_code.split ('.')[0]
                const parentIndex = values.permissions.findIndex (p => p.area_code === parentAreaCode)

                if (parentIndex !== -1 && !values.permissions[parentIndex].selected) {
                  const updatedPermissions = values.permissions.map ((p, i) => {
                    if (i === parentIndex) return { ...p, selected: true }
                    if (i === index) return { ...p, selected: true }
                    return p
                  })
                  setFieldValue ('permissions', updatedPermissions)
                  return
                }
                setFieldValue (`permissions[${index}].selected`, true)
              }
            }

            const handlePermissionChanged = (field, value, permission, index) => {
              const isChild = permission.area_code.includes ('.')
              const isParent = !isChild

              setFieldValue (`permissions[${index}].${field}`, value)

              if (value === true && !values.permissions[index].selected) {
                setFieldValue (`permissions[${index}].selected`, true)

                if (isChild) {
                  const parentAreaCode = permission.area_code.split ('.')[0]
                  const parentIndex = values.permissions.findIndex (p => p.area_code === parentAreaCode)
                  if (parentIndex !== -1 && !values.permissions[parentIndex].selected) {
                    setFieldValue (`permissions[${parentIndex}].selected`, true)
                  }
                }
              }

              if (isParent) {
                const updatedPermissions = values.permissions.map ((p, i) => {
                  if (i === index) {
                    return value === true
                      ? { ...p, [field]: true, selected: true }
                      : { ...p, [field]: false }
                  }
                  if (p.area_code.startsWith (permission.area_code + '.')) {
                    return value === true
                      ? { ...p, [field]: true, selected: true }
                      : { ...p, [field]: false }
                  }
                  return p
                })
                setFieldValue ('permissions', updatedPermissions)
              }

              if (isChild && value === false) {
                const parentAreaCode = permission.area_code.split ('.')[0]
                const siblings = values.permissions.filter (
                  p => p.area_code.startsWith(parentAreaCode + '.') && p.area_code !== permission.area_code
                )
                const allSiblingsUnselected = siblings.every (sib => !sib[field])
                const parentIndex = values.permissions.findIndex (p => p.area_code === parentAreaCode)

                if (allSiblingsUnselected && parentIndex !== -1) {
                  setFieldValue (`permissions[${parentIndex}].${field}`, false)
                }
              }

              if (isChild && value === true) {
                const parentAreaCode = permission.area_code.split ('.')[0]
                const parentIndex = values.permissions.findIndex (p => p.area_code === parentAreaCode)
                if (parentIndex !== -1 && !values.permissions[parentIndex][field]) {
                  setFieldValue (`permissions[${parentIndex}].${field}`, true)
                }
              }
            }

            const handleEditPermissionChanged = (value, permission, index) => {
              handlePermissionChanged ('allow_edit', value, permission, index)
            }

            const handleDeletePermissionChanged = (value, permission, index) => {
              handlePermissionChanged ('allow_delete', value, permission, index)
            }

            return (
              <>
                <DialogBody>
                  <Form
                    onSubmit={handleSubmit}
                    onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}
                    style={{paddingBottom: accessLevel?.codename === 'service' ? 0 : 10}}
                  >
                    <FormField>
                      <Label htmlFor='name'>
                        Nome
                      </Label>
                      <Input
                        id='name'
                        name='name'
                        placeholder='Nome do nível de acesso'
                        value={values.name}
                        disabled={!canEdit}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.name && errors.name && (
                        <WarningText>
                          {errors.name}
                        </WarningText>
                      )}
                    </FormField>
                    <FormField>
                      <Label htmlFor='description'>
                        Descrição
                      </Label>
                      <Input
                        id='description'
                        name='description'
                        placeholder='Descrição'
                        value={values.description}
                        disabled={!canEdit}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.description && errors.description && (
                        <WarningText>
                          {errors.description}
                        </WarningText>
                      )}
                    </FormField>
                    {accessLevel?.codename !== 'service' && (
                      <>
                        <SubTitle style={{marginBottom: 10}}>
                          Permissões
                        </SubTitle>
                        {canEdit && (
                          <SmallButton onClick={toggleImportPermissionsModal}>
                            <FaFileImport size={14} color='#FFC100'/>
                            Importar permissões
                          </SmallButton>
                        )}
                        <CheckRow style={{paddingInline: 10}}>
                          <SubTitle>Área</SubTitle>
                          <SubTitle>Visualizar</SubTitle>
                          <SubTitle>Editar</SubTitle>
                          <SubTitle>Excluir</SubTitle>
                        </CheckRow>
                        <PermissionsContainer>
                          {values.permissions.map ((permission, index) => {
                            const isChild = permission.area_code.includes ('.')

                            return (
                              <CheckRow key={permission.area_code} isChild={isChild} index={index}>
                                <Label
                                  htmlFor={`permissions.${index}.selected`}
                                  style={{fontWeight: isChild ? 200 : 500}}
                                >
                                  {permission.area_name}
                                </Label>
                                <CheckBox
                                  id={`permissions.${index}.selected`}
                                  value={permission.selected}
                                  onValueChange={value => handleViewPermissionChanged (value, permission, index)}
                                  disabled={!canEdit}
                                />
                                {/* Só "Visualizar" vale nesta área (ver `isViewOnlyArea`): no lugar de editar e excluir, espaço vazio */}
                                {isViewOnlyArea(permission.area_code) ? (
                                  <>
                                    <span/>
                                    <span/>
                                  </>
                                ) : (
                                  <>
                                    <CheckBox
                                      id={`permissions.${index}.allow_edit`}
                                      value={permission.allow_edit}
                                      onValueChange={value => handleEditPermissionChanged (value, permission, index)}
                                      disabled={!canEdit}
                                    />
                                    <CheckBox
                                      id={`permissions.${index}.allow_delete`}
                                      value={permission.allow_delete}
                                      onValueChange={value => handleDeletePermissionChanged (value, permission, index)}
                                      disabled={!canEdit}
                                    />
                                  </>
                                )}
                              </CheckRow>
                            )}
                          )}
                        </PermissionsContainer>
                        <ImportPermissionsModal
                          visible={showImportPermissionsModal}
                          accessLevels={accessLevels}
                          onConfirm={importedPermissions => {
                            setFieldValue ('permissions', permissions.map (permission => {
                              const existingPermission = importedPermissions.find (p => p.id === permission.id)
                              return existingPermission
                                ? { ...existingPermission, selected: true }
                                : { ...permission, selected: false }
                            }))
                          }}
                          onClose={toggleImportPermissionsModal}
                        />
                      </>
                    )}
                    {errorMessage && (
                      <ErrorContainer>
                        <p>{errorMessage}</p>
                      </ErrorContainer>
                    )}
                  </Form>
                </DialogBody>
                {dirty && (
                  <DialogFooter>
                    <MainButton
                      type='button'
                      label={isEditting ? 'Salvar alterações' : 'Confirmar'}
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      style={{alignSelf: 'center'}}
                      width={140}
                    />
                  </DialogFooter>
                )}
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
